import React from 'react';
import { InfoTooltip, useStyles } from '@webapps/styles';
import { DateRangeRounded, MenuBookRounded, SchoolRounded } from '@material-ui/icons';
import { Button, Avatar } from '@material-ui/core';
import { useAuth, useQuery } from '@webapps/reactjs-tech-components';
import { getInfosDashboard, searchTrainingsByRelations } from '@webapps/business';
import { useHistory } from 'react-router-dom';
import { ROUTES_PATH } from 'config/constants';
import { formatDate224, getTrainingStatusChip } from '@webapps/utils';
import theme from '../theme';
import Nodata from 'components/NoData';

export default function LastTrainings() {
  const { classes, global } = useStyles(styles);

  const { idUser } = useAuth();
  const { push } = useHistory();

  const {
    images: { Trainings }
  } = theme

  const { data: { content: trainings = [], totalElements } = {} } = useQuery(searchTrainingsByRelations, {
    key: ['lastTrainings', idUser],
    variables: {
      id: idUser,
      size: 5,
      page: 0
    },
    keepPreviousData: true
  });

  const TrainingRow = ({ info }) => {
    const { classes } = useStyles(styles);
    const { push } = useHistory();

    const { data: dashboardInfo = {} } = useQuery(getInfosDashboard, {
      key: ['infoDashboardSchool', info?.training?.school?.id],
      variables: { id: info?.training?.school?.id },
      enabled: !!info?.training?.school?.id,
    });

    const profileImage = dashboardInfo?.profile?.profileImageUrl;

    return (
      <div className={classes.itemCard} onClick={() => push(ROUTES_PATH.CFA_ORGANISMES, { toTraining: true, label: info?.training?.label })}>
        <div className={classes.itemContent}>
          {
            profileImage ? (
              <Avatar className={classes.avatar} src={profileImage} />
            ) : (
              <Avatar className={classes.avatar}>
                {info?.training?.school?.name?.substr(0, 1)}
              </Avatar>
            )
          }
          <div className={classes.itemInfo}>
            <div className={classes.itemName}>{info?.training?.label}</div>
            <div className={classes.schoolInfo}>
              <div className={classes.schoolInfoText}>{info?.training?.school?.name || "École non renseignée"}</div>
            </div>
            <div className={classes.meta}>
              <DateRangeRounded className={classes.metaIcon} />
              <div className={classes.metaText}>{`Dernière modification le ${formatDate224(info?.training?.lastUpdate)}`}</div>
            </div>
          </div>
        </div>
        <div className={classes.itemStatus}>
          {getTrainingStatusChip(info?.training)}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={global.title}>5 dernières formations</div>
          <InfoTooltip
            position="bottom"
            forceVisible={false}
            title="Dernières formations"
            description="Affiche les 5 dernières formations ajoutées ou modifiées."
            link={{ text: "En savoir plus", onClick: () => push(ROUTES_PATH.CFA_ORGANISMES, { toTraining: true }) }}
          />
        </div>
        <div className={classes.listContainer}>
          {trainings?.length > 0 ? (
            trainings?.map((info) => (
              <TrainingRow key={info.id} info={info} />
            ))
          ) : (
            <Nodata
              image={Trainings}
              contentWidth="70%"
              padding="0px 20px 20px 0px"
              title="Aucune formation trouvée"
              description="Les 5 dernières formations s'afficheront ici."
            />
          )}
        </div>
      </div>
      {(trainings?.length > 0) && (
        <Button className={classes.viewAllButton} onClick={() => push(ROUTES_PATH.CFA_ORGANISMES, { toTraining: true })}>
          <p className={classes.viewAllText}>Tout voir</p>
        </Button>
      )}
    </div>
  );
}

const styles = ({ spacing }) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    flex: 1,
    padding: '20px',
    gap: '20px',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    overflow: 'visible',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  itemCard: {
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '10px',
    justifyContent: 'space-between',
    padding: '16px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#f3f7ff',
      '& p': {
        color: '#0060f9',
      },
    },
    '@media (max-width: 1250px)': {
      flexDirection: 'column',
      gap: '6px',
      alignItems: 'flex-start',
    },
    '@media (max-width: 1024px)': {
      flexDirection: 'row',
      gap: 'initial',
      alignItems: 'center'
    }
  },
  itemStatus: {
    '@media (max-width: 1250px)': {
      marginLeft: '76px' // 60px Image + 16px Gap
    },
    '@media (max-width: 1024px)': {
      marginLeft: '0px'
    }
  },
  itemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    overflow: 'hidden',
    minWidth: 0,
    width: '100%',
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    overflow: 'hidden',
    minWidth: 0,
    flex: 1,
    width: 0,
  },
  itemName: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '14px',
    color: '#152652',
    margin: '0px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  schoolInfo: {
    backgroundColor: '#0060f9',
    borderRadius: '5px',
    display: 'flex',
    padding: '4px',
    width: 'fit-content',
  },
  schoolInfoText: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '12px',
    lineHeight: '12px',
    textAlign: 'center',
    color: '#ffffff',
  },
  meta: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    overflow: 'hidden',
    width: '100%',
  },
  metaIcon: {
    height: '14px',
    width: '14px',
    fill: '#5d75a8',
  },
  metaText: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '12px',
    lineHeight: '12px',
    color: '#5d75a8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  avatar: {
    height: 60,
    width: 60,
    minWidth: 60,
    border: '1px solid #C0D9FF',
  },
  viewAllButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #c0d9ff',
    borderRadius: '0px',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    flexDirection: 'row',
    height: '40px',
    padding: '10px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#ffffff',
      '& p': {
        color: '#152652',
      },
    },
  },
  viewAllText: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '13.5px',
    textAlign: 'center',
    color: '#5d75a8',
    margin: 0,
  },
});